<div class="signals-editor">

  @if (isSystemProfile) {
    <div class="alert alert-warning py-2 mb-3" role="alert">
      {{ 'i18n.domain.sampling-profiles.system-profile-warning' | translate }}
    </div>
  }

  <!-- Availability context: verdicts are per collector installation, so they need a vehicle. -->
  <div class="row align-items-end g-2 mb-3">
    <div class="col-md-6">
      <label class="form-label">{{ 'i18n.domain.sampling-profiles.signals.add-signal' | translate }}</label>
      <app-dynamic-autocomplete
        [getDataFunction]="searchSignals"
        [enabled]="!disabled"
        [isFormComponent]="true"
        [formControl]="signalPickerControl"
        placeholder="i18n.domain.sampling-profiles.signals.search-placeholder">
      </app-dynamic-autocomplete>
    </div>

    <div class="col-md-6">
      @if (hasAvailabilityContext) {
        <label class="form-label">{{ 'i18n.domain.sampling-profiles.availability.for-vehicle' | translate }}</label>
        <ejs-dropdownlist
          [dataSource]="vehicleOptions"
          [fields]="vehicleFields"
          [value]="selectedVehicleId"
          [showClearButton]="true"
          placeholder="{{ 'i18n.domain.sampling-profiles.availability.select-vehicle' | translate }}"
          (change)="onVehicleSelected($event.value)">
        </ejs-dropdownlist>
      } @else {
        <div class="text-muted small">
          {{ 'i18n.domain.sampling-profiles.availability.no-vehicle-assigned' | translate }}
        </div>
      }
    </div>
  </div>

  <!-- Roll-up so the user sees the headline before scanning individual rows. -->
  @if (availability) {
    <div class="d-flex flex-wrap align-items-center gap-2 mb-2">
      <span class="fw-semibold">
        {{ 'i18n.domain.sampling-profiles.availability.summary' | translate: {
              available: availability.availableCount,
              configured: availability.configuredSignalCount
            } }}
      </span>
      @if (availability.unavailableCount) {
        <span class="badge bg-danger">
          {{ 'i18n.domain.sampling-profiles.availability.unavailable-count' | translate: { count: availability.unavailableCount } }}
        </span>
      }
      @if (availability.intermittentCount) {
        <span class="badge bg-warning text-dark">
          {{ 'i18n.domain.sampling-profiles.availability.intermittent-count' | translate: { count: availability.intermittentCount } }}
        </span>
      }
      @if (availability.unknownCount) {
        <span class="badge bg-secondary">
          {{ 'i18n.domain.sampling-profiles.availability.unknown-count' | translate: { count: availability.unknownCount } }}
        </span>
      }
    </div>

    @if (!availability.hasActiveInstallation) {
      <div class="alert alert-info py-2" role="alert">
        {{ 'i18n.domain.sampling-profiles.availability.no-active-installation' | translate }}
      </div>
    }

    @if (availability.staleCount) {
      <div class="alert alert-warning py-2" role="alert">
        {{ 'i18n.domain.sampling-profiles.availability.stale-warning' | translate: { count: availability.staleCount } }}
      </div>
    }
  }

  @if (availabilityLoading) {
    <div class="text-muted small mb-2">{{ 'i18n.domain.sampling-profiles.availability.loading' | translate }}</div>
  }

  @if (signals.length === 0) {
    <div class="text-muted">{{ 'i18n.domain.sampling-profiles.signals.empty' | translate }}</div>
  } @else {
    <div class="table-responsive signals-table-wrapper">
      <table class="table table-sm align-middle">
        <thead>
          <tr>
            <th scope="col">{{ 'i18n.domain.sampling-profiles.signals.signal' | translate }}</th>
            <th scope="col" class="col-enabled">{{ 'i18n.domain.sampling-profiles.signals.enabled' | translate }}</th>
            <th scope="col" class="col-mode">{{ 'i18n.domain.sampling-profiles.signals.collection-mode' | translate }}</th>
            <th scope="col" class="col-interval">{{ 'i18n.domain.sampling-profiles.signals.interval' | translate }}</th>
            <th scope="col" class="col-availability">{{ 'i18n.domain.sampling-profiles.signals.availability' | translate }}</th>
            <th scope="col" class="col-actions"></th>
          </tr>
        </thead>
        <tbody>
          @for (signal of signals; track signal.signalId) {
            <tr>
              <td>
                <span class="signal-name">{{ signalLabel(signal) }}</span>
                @if (signal.unit) {
                  <span class="text-muted small ms-1">({{ signal.unit }})</span>
                }
                @if (signal.category) {
                  <div class="text-muted small">{{ signal.category }}</div>
                }
              </td>
              <td>
                <ejs-checkbox
                  [checked]="signal.enabled"
                  [disabled]="disabled"
                  (change)="onEnabledChanged(signal, $event.checked)">
                </ejs-checkbox>
              </td>
              <td>
                <ejs-dropdownlist
                  [dataSource]="collectionModeOptions"
                  [fields]="collectionModeFields"
                  [value]="signal.collectionMode"
                  [enabled]="!disabled"
                  (change)="onCollectionModeChanged(signal, $event.value)">
                </ejs-dropdownlist>
              </td>
              <td>
                @if (isIntervalEditable(signal)) {
                  <ejs-numerictextbox
                    [value]="signal.samplingIntervalMs"
                    [min]="1"
                    [format]="'n0'"
                    [showSpinButton]="false"
                    (change)="onIntervalChanged(signal, $event.value)">
                  </ejs-numerictextbox>
                } @else {
                  <span class="text-muted">&mdash;</span>
                }
              </td>
              <td>
                @if (hasAvailabilityContext && selectedVehicleId) {
                  <span [class]="availabilityBadgeClass(signal)" [attr.title]="availabilityTooltip(signal)">
                    {{ availabilityLabelKey(signal) | translate }}
                  </span>
                  @if (isStale(signal)) {
                    <span class="text-muted small ms-1"
                          [attr.title]="'i18n.domain.sampling-profiles.availability.stale-hint' | translate">*</span>
                  }
                } @else {
                  <span class="text-muted">&mdash;</span>
                }
              </td>
              <td class="text-end">
                <button type="button"
                        class="btn btn-sm btn-link text-danger p-0"
                        [disabled]="disabled"
                        [attr.title]="'i18n.domain.sampling-profiles.signals.remove' | translate"
                        (click)="removeSignal(signal.signalId!)">
                  <span class="e-icons icon-delete"></span>
                </button>
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  }
</div>
